import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, X, Sparkles, BarChart3, Megaphone, Sliders } from "lucide-react";
import useCookieConsent, {
  DEFAULT_CATEGORIES,
} from "@/hooks/useCookieConsent";

/**
 * Banner cookie conforme al provvedimento del Garante privacy del 10 giugno 2021:
 * - Tre azioni con pari dignità visiva: "Accetta tutti", "Rifiuta tutti", "Personalizza".
 * - Chiudere con X equivale a "Rifiuta tutti" (non è più valido lo scroll = accettazione).
 * - Nessun cookie di profilazione viene attivato PRIMA della scelta esplicita.
 * - Riapribile dal footer (link "Preferenze cookie") o via evento `cookie:open`.
 * - Persistenza 6 mesi (readConsent invalida oltre TTL, forzando ri-consenso).
 */

const CATEGORY_META = {
  necessary: {
    icon: Shield,
    title: "Necessari",
    body: "Cookie tecnici indispensabili per il funzionamento del sito (login, sessione, sicurezza). Non richiedono consenso ex art. 6 §1 f GDPR.",
    locked: true,
  },
  preferences: {
    icon: Sliders,
    title: "Preferenze",
    body: "Ricordano le tue scelte di interfaccia (lingua, aziende attive, filtri) per non doverle reinserire ad ogni visita.",
    locked: false,
  },
  statistics: {
    icon: BarChart3,
    title: "Statistiche anonime",
    body: "Ci aiutano a capire come viene usata l'app (pagine più viste, punti di abbandono) senza mai identificarti personalmente.",
    locked: false,
  },
  marketing: {
    icon: Megaphone,
    title: "Marketing",
    body: "Servirebbero per mostrarti annunci più pertinenti al di fuori del sito. Attualmente non ne utilizziamo.",
    locked: false,
  },
};

export default function CookieConsent() {
  const { hasConsented, acceptAll, rejectAll, savePartial } = useCookieConsent();
  const [view, setView] = useState("banner"); // "banner" | "custom" | "hidden"
  const [prefs, setPrefs] = useState(DEFAULT_CATEGORIES);

  // Mostra banner alla prima visita.
  useEffect(() => {
    setView(hasConsented ? "hidden" : "banner");
  }, [hasConsented]);

  // Riapertura da footer o codice esterno.
  useEffect(() => {
    const open = () => {
      setPrefs((p) => ({ ...DEFAULT_CATEGORIES, ...p }));
      setView("custom");
    };
    window.addEventListener("cookie:open", open);
    return () => window.removeEventListener("cookie:open", open);
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    setView("hidden");
  }, [acceptAll]);

  const handleRejectAll = useCallback(() => {
    rejectAll();
    setView("hidden");
  }, [rejectAll]);

  const handleSave = useCallback(() => {
    savePartial(prefs);
    setView("hidden");
  }, [savePartial, prefs]);

  const openCustom = useCallback(() => {
    setView("custom");
  }, []);

  if (view === "hidden") return null;

  return (
    <>
      {view === "banner" && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          data-testid="cookie-banner"
          className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[440px] z-[100] rounded-2xl bg-white border border-[#E8E4D9] shadow-[0_20px_50px_-15px_rgba(10,10,10,0.35)] p-5 md:p-6 animate-in fade-in slide-in-from-bottom-4"
        >
          <button
            type="button"
            onClick={handleRejectAll}
            aria-label="Chiudi e rifiuta tutti i cookie non necessari"
            data-testid="cookie-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center text-[#737373] hover:bg-[#F5F2EA] hover:text-[#0A0A0A] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#F5F2EA] grid place-items-center">
              <Cookie className="w-4 h-4 text-[#B8985C]" />
            </div>
            <h2 id="cookie-banner-title" className="font-display font-extrabold text-[#0A0A0A] text-lg tracking-tight">
              Privacy prima di tutto
            </h2>
          </div>

          <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4">
            Usiamo cookie tecnici indispensabili al login e alla sessione. Prima di attivare
            statistiche anonime o preferenze devi dirci sì. Zero profilazione, zero pixel di
            terze parti.{" "}
            <Link
              to="/cookies"
              className="font-semibold text-[#1D4ED8] hover:underline"
              data-testid="cookie-link-policy"
            >
              Leggi la Cookie Policy
            </Link>
            .
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={handleAcceptAll}
              data-testid="cookie-accept-all"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors shadow-sm"
            >
              <Sparkles className="w-3.5 h-3.5" /> Accetta tutti
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              data-testid="cookie-reject-all"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-white text-[#0A0A0A] border border-[#0A0A0A]/20 text-sm font-bold hover:bg-[#F5F2EA] transition-colors"
            >
              Rifiuta tutti
            </button>
            <button
              type="button"
              onClick={openCustom}
              data-testid="cookie-customize"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-[#4A4A4A] text-sm font-semibold hover:bg-[#F5F2EA] hover:text-[#0A0A0A] transition-colors"
            >
              Personalizza
            </button>
          </div>
        </div>
      )}

      {view === "custom" && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-modal-title"
          data-testid="cookie-modal"
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          {/* Backdrop */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-[#0A0A0A]/60 backdrop-blur-sm"
            onClick={handleRejectAll}
          />

          {/* Panel */}
          <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl border border-[#E8E4D9] shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-[#E8E4D9]">
              <div>
                <h2 id="cookie-modal-title" className="font-display font-extrabold text-xl text-[#0A0A0A] tracking-tight">
                  Preferenze cookie
                </h2>
                <p className="text-xs text-[#737373] mt-1">
                  Scegli quali categorie autorizzare. Puoi cambiare idea in qualsiasi momento dal footer.
                </p>
              </div>
              <button
                type="button"
                onClick={handleRejectAll}
                aria-label="Chiudi e rifiuta"
                data-testid="cookie-modal-close"
                className="w-8 h-8 shrink-0 rounded-full grid place-items-center text-[#737373] hover:bg-[#F5F2EA] hover:text-[#0A0A0A] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Categories */}
            <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const checked = meta.locked ? true : Boolean(prefs[key]);
                return (
                  <div
                    key={key}
                    className={`rounded-2xl border p-4 ${
                      meta.locked ? "bg-[#F5F2EA]/50 border-[#E8E4D9]" : "bg-white border-[#E8E4D9]"
                    }`}
                    data-testid={`cookie-cat-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 shrink-0 rounded-full bg-[#F5F2EA] grid place-items-center">
                        <Icon className="w-4 h-4 text-[#B8985C]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-[#0A0A0A] text-sm">{meta.title}</span>
                            {meta.locked && (
                              <span className="text-[10px] uppercase tracking-wider font-mono bg-[#0A0A0A]/8 text-[#0A0A0A] px-1.5 py-0.5 rounded">
                                Sempre attivi
                              </span>
                            )}
                          </div>
                          {/* Toggle */}
                          <button
                            type="button"
                            role="switch"
                            aria-checked={checked}
                            aria-label={`Attiva/disattiva ${meta.title}`}
                            disabled={meta.locked}
                            onClick={() => setPrefs((p) => ({ ...p, [key]: !p[key] }))}
                            data-testid={`cookie-toggle-${key}`}
                            className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              checked ? "bg-emerald-600" : "bg-[#E8E4D9]"
                            } ${meta.locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                                checked ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                        <p className="text-xs text-[#4A4A4A] leading-relaxed">{meta.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#E8E4D9] p-4 sm:p-5 flex flex-col sm:flex-row gap-2 sm:justify-end bg-[#FAF9F6]">
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-modal-reject"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-white text-[#0A0A0A] border border-[#0A0A0A]/20 text-sm font-bold hover:bg-[#F5F2EA] transition-colors"
              >
                Rifiuta tutti
              </button>
              <button
                type="button"
                onClick={handleSave}
                data-testid="cookie-modal-save"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors shadow-sm"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="cookie-modal-accept-all"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm"
              >
                <Sparkles className="w-3.5 h-3.5" /> Accetta tutti
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
